import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';

type CardLayout = 'auto' | 'small' | 'large';

const CardLogo = styled.div`
  width: ${remCalc(64)};
  height: ${remCalc(64)};
  border-radius: 100%;
  overflow: hidden;
  box-shadow: rgb(0 27 56 / 16%) 0px 4px 16px;
  margin-bottom: ${remCalc(20)};
`;

const Title = styled.p`
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.NORMAL};
  margin-bottom: ${remCalc(4)};

  small {
    display: block;
    ${customTextSize(18, 30)};
  }
`;

const Text = styled.p`
  ${customTextSize(14, 16)}
`;

const smallCardStyles = css`
  align-items: center;
  flex-direction: row;

  ${CardLogo} {
    margin-bottom: 0;
    margin-right: ${remCalc(16)};
  }
`;

const largeCardStyles = css`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${remCalc(220)};

  text-align: center;

  ${CardLogo} {
    margin-bottom: ${remCalc(16)};
    margin-right: 0;
  }

  ${Title} {
    margin-bottom: ${remCalc(8)};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
  }
`;

interface CardProps {
  layout: CardLayout;
}

const Card = styled.div<CardProps>`
  padding: ${remCalc(16)};
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  box-shadow: rgb(0 27 56 / 10%) 0px 0px 30px;

  transition: background-color 0.2s ease-in-out;

  display: flex;

  ${smallCardStyles}

  ${(props) =>
    props.layout === 'auto' &&
    css`
      ${mediaBreakpointUp('desktop')} {
        ${largeCardStyles}
      }
    `}

  ${(props) => props.layout === 'large' && largeCardStyles}

  &:hover {
    background-color: ${COLORS.GRAY.HIGHLIGHT};
  }
`;

interface AppCardProps {
  title: string;
  text?: string;
  image?: ReactNode;
  layout?: CardLayout;
}

const AppCard: FC<AppCardProps> = (props) => {
  return (
    <Card layout={props.layout ?? 'auto'}>
      {!!props.image && <CardLogo>{props.image}</CardLogo>}

      <div>
        <Title>{props.title}</Title>

        {!!props.text && <Text>{props.text}</Text>}
      </div>
    </Card>
  );
};

export default AppCard;
